import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Loader2, Clock } from "lucide-react";

/* Activity Timeline tab — the only GA tab in BP-001B.

   Reads immutable events from `/api/v3/properties/{id}/timeline`.
   No writes.
*/
export default function TimelineTab({ property }) {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(
        `/v3/properties/${property.id}/timeline?limit=100`,
      );
      setEvents(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load timeline");
    } finally {
      setLoading(false);
    }
  }, [property.id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="timeline-loading">
        <Loader2 size={14} className="animate-spin" /> Loading timeline…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-md border border-red-300 bg-red-500/5 p-4 text-sm text-red-800"
           data-testid="timeline-error">
        {error}
      </div>
    );
  }
  if (!events.length) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-600"
           data-testid="timeline-empty">
        <Clock size={20} className="mx-auto mb-2 text-slate-500" />
        No activity yet. Changes to this property will appear here.
      </div>
    );
  }

  return (
    <div data-testid="tab-panel-timeline">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-500">
          {total} event{total === 1 ? "" : "s"} — immutable
        </div>
        <button
          onClick={load}
          data-testid="timeline-refresh"
          className="text-[11px] px-2 py-1 rounded border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300"
        >
          Refresh
        </button>
      </div>
      <ol className="relative border-l border-slate-200 pl-6 space-y-4" data-testid="timeline-list">
        {events.map((e) => (
          <li key={e.event_id} className="relative" data-testid={`timeline-event-${e.event_code}`}>
            <div className="absolute -left-[27px] top-1.5 h-2 w-2 rounded-full bg-amber-400/80 ring-4 ring-amber-500/10" />
            <div className="text-[13px] text-slate-900 font-medium">{e.event_label}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {new Date(e.occurred_at).toLocaleString()}
              {e.actor_type === "system" ? " · system" : e.actor_id ? " · user" : ""}
            </div>
            {e.payload && Object.keys(e.payload).length > 0 ? (
              <details className="mt-1.5 group">
                <summary className="text-[10px] uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-700">
                  payload
                </summary>
                <pre className="mt-1 text-[11px] leading-relaxed p-2 rounded bg-white border border-slate-100 text-slate-600 overflow-auto">
{JSON.stringify(e.payload, null, 2)}
                </pre>
              </details>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}
